// Unit Presence — Minority Report style: large, visual, understood at a glance.
// Size = importance. Ring = health/progress. Motion = activity level.
// You should understand the state of each unit WITHOUT reading.

import React, { useRef, useState, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';

// ============================================================
// Types
// ============================================================

export enum PresenceShape {
  PERSON = 'person',
  DEAL = 'deal',
  DOCUMENT = 'document',
  PROJECT = 'project',
}

/** What kind of card layout this unit renders */
export enum UnitType {
  DEAL = 'deal',           // dollar value, health ring, sales-oriented
  PROJECT = 'project',     // progress bar, percent complete, milestones
  PERSON = 'person',       // avatar, role/title, relationship context
  DOCUMENT = 'document',   // doc icon, status, last editor
  INITIATIVE = 'initiative', // strategic item, OKR-style progress
  GENERIC = 'generic',     // fallback — name, subtitle, heat-based ring
}

export enum CognitiveZone {
  FOCUSED_EXECUTION = 'focused_execution',
  REFLECTIVE_THINKING = 'reflective_thinking',
  REACTIVE_TRIAGE = 'reactive_triage',
  COLLABORATIVE_WAITING = 'collaborative_waiting',
}

export interface ChildPresence {
  id: string;
  name: string;
  type: PresenceShape;
  heat: number;
  isGhost: boolean;
  scale?: number;
  initials?: string;
  label?: string;
  offset: [number, number];
  color: string;
}

export interface UnitData {
  id: string;
  name: string;
  position: [number, number, number];
  heat: number;          // 0-1: drives size, ring fullness, glow intensity
  isGhost: boolean;
  color: string;
  scale?: number;
  subtitle?: string;
  /** Card layout type — determines what the card shows */
  unitType?: UnitType;
  /** 0-1: how complete/healthy is this unit */
  health?: number;
  /** Primary metric — dollar value, page count, whatever fits */
  value?: string;
  /** Small label above the value, e.g. "Revenue", "Pages", "Progress" */
  valueLabel?: string;
  /** 0-1: explicit progress (different from health — health is status, progress is completion) */
  progress?: number;
  /** Short status tag, e.g. "On Track", "Draft", "Overdue" */
  statusTag?: string;
  /** Icon/emoji for the ring center instead of child count */
  icon?: string;
  /** Which cognitive zone this card instance lives in */
  zone: CognitiveZone;
  /** Why this unit is in this zone — shown as subtitle on the card */
  facetLabel?: string;
  /** 0-1 multiplier computed by layout solver so cards fill zone without overlapping */
  cardScale?: number;
  /** Relative time label for last activity, e.g. "2m ago", "3h ago" */
  lastActivity?: string;
  /** Whether something changed recently — shows a pulse dot */
  recentChange?: boolean;
  children: ChildPresence[];
}

// ============================================================
// Unit — large visual panel
// ============================================================

interface UnitPresenceProps {
  data: UnitData;
  cameraZ: number;
  isExpanded: boolean;
  dimmed?: boolean;
  selected?: boolean;
  entranceDelay?: number; // seconds — stagger entrance
  onSelect?: (id: string) => void;
  onDragStart?: (id: string, e: React.PointerEvent) => void;
}

export default function UnitPresence({ data, cameraZ, isExpanded, dimmed, selected, entranceDelay = 0, onSelect, onDragStart }: UnitPresenceProps) {
  const groupRef = useRef<THREE.Group>(null);
  const time = useRef(Math.random() * 100);
  const entranceProgress = useRef(0);
  const entranceWait = useRef(entranceDelay);
  const [hovered, setHovered] = useState(false);

  // Heat drives EVERYTHING visual
  // Size: hot = big (340px), cold = smaller (220px)
  // cardScale shrinks the entire card via CSS transform so it fits the zone
  const cs = data.cardScale ?? 1;
  const baseWidth = 220 + data.heat * 120;
  const baseHeight = baseWidth * 0.65;

  // Health ring: how full is the arc (0 to 1)
  const health = data.health ?? data.heat;

  // Status color — intuitive, not decorative
  // Green = healthy/on-track, Amber = watch, Red = needs attention
  const statusColor = health > 0.65 ? '#2d8a4e' : health > 0.35 ? '#b5880a' : '#c4392d';
  const statusBg = health > 0.65 ? 'rgba(45,138,78,0.08)' : health > 0.35 ? 'rgba(181,136,10,0.06)' : 'rgba(196,57,45,0.06)';

  // Motion: subtle wiggle/vibrate, NOT drifting. Hot = more frequent micro-tremors.
  // Cold items are nearly still. This should feel like nervous energy, not floating.
  const wiggleAmplitude = 0.003 + data.heat * 0.008; // tiny pixel-level displacement
  const wiggleSpeed = 2 + data.heat * 6; // hot items vibrate faster

  useFrame((_, delta) => {
    time.current += delta;

    // Entrance animation: wait for delay, then ramp 0→1 over 0.45s
    if (entranceProgress.current < 1) {
      if (entranceWait.current > 0) {
        entranceWait.current -= delta;
      } else {
        entranceProgress.current = Math.min(1, entranceProgress.current + delta / 0.45);
      }
    }
    const ep = entranceProgress.current;
    // Ease out cubic
    const ease = 1 - Math.pow(1 - ep, 3);

    if (groupRef.current) {
      // Combine two fast sine waves at slightly different frequencies for organic tremor
      const t = time.current;
      const wiggleX = (Math.sin(t * wiggleSpeed) + Math.sin(t * wiggleSpeed * 1.7 + 0.5)) * wiggleAmplitude * 0.5;
      const wiggleY = (Math.cos(t * wiggleSpeed * 0.9) + Math.cos(t * wiggleSpeed * 1.4 + 1.2)) * wiggleAmplitude * 0.5;
      groupRef.current.position.x = data.position[0] + wiggleX;
      groupRef.current.position.y = data.position[1] + wiggleY;
      // Scale entrance
      const s = 0.6 + 0.4 * ease;
      groupRef.current.scale.setScalar(s);
    }
  });

  const baseOpacity = data.isGhost ? 0.25 : 0.7 + data.heat * 0.3;
  const opacity = dimmed ? baseOpacity * 0.2 : baseOpacity;

  // SVG ring parameters
  const ringSize = 52 + data.heat * 20;
  const ringStroke = 3.5;
  const ringRadius = (ringSize - ringStroke) / 2;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringDashoffset = ringCircumference * (1 - health);

  return (
    <group ref={groupRef} position={data.position}>
      {!isExpanded && (
        <Html center distanceFactor={8} style={{ pointerEvents: 'auto' }}>
          <div style={{
            transform: cs < 1 ? `scale(${cs})` : undefined,
            transformOrigin: 'center center',
          }}>
          <div
            onClick={(e) => { e.stopPropagation(); onSelect?.(data.id); }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
              width: baseWidth,
              minHeight: baseHeight,
              padding: '18px 22px',
              borderRadius: 14,
              background: selected
                ? `linear-gradient(135deg, white, ${statusBg})`
                : hovered
                ? `linear-gradient(135deg, #fafbff, ${statusBg})`
                : `linear-gradient(135deg, white, ${statusBg})`,
              border: selected
                ? `2px solid ${statusColor}`
                : `1px solid ${hovered ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
              boxShadow: selected
                ? [
                    `0 4px 20px rgba(0,0,0,0.12)`,
                    `0 0 0 3px ${statusColor}20`,
                  ].join(', ')
                : hovered
                ? [
                    `0 ${6 + data.heat * 8}px ${16 + data.heat * 24}px rgba(0,0,0,${0.07 + data.heat * 0.1})`,
                    `0 0 0 1px rgba(0,0,0,0.05)`,
                  ].join(', ')
                : [
                    `0 ${2 + data.heat * 6}px ${8 + data.heat * 20}px rgba(0,0,0,${0.04 + data.heat * 0.08})`,
                    `0 0 0 1px rgba(0,0,0,0.03)`,
                  ].join(', '),
              cursor: 'pointer',
              opacity,
              transform: hovered && !selected ? 'translateY(-2px)' : selected ? 'translateY(-1px)' : undefined,
              transition: 'all 0.2s ease',
              position: 'relative' as const,
              display: 'flex',
              flexDirection: 'column' as const,
              gap: 8,
            }}
          >
            {/* Drag handle — top-right grip */}
            {onDragStart && (
              <div
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onDragStart(data.id, e);
                }}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 22,
                  height: 22,
                  borderRadius: 5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'grab',
                  opacity: hovered ? 0.4 : 0,
                  transition: 'opacity 0.15s',
                  color: '#999',
                  fontSize: 11,
                  background: 'rgba(0,0,0,0.03)',
                }}
                title="Drag to move zone"
              >
                ⠿
              </div>
            )}

            {/* ===== Type-switched card content ===== */}
            <CardContent data={data} statusColor={statusColor} health={health} ringSize={ringSize} ringStroke={ringStroke} ringRadius={ringRadius} ringCircumference={ringCircumference} ringDashoffset={ringDashoffset} />
          </div>
          </div>
        </Html>
      )}

      {/* Expanded — children as individual cards */}
      {isExpanded && (
        <group>
          <Html center distanceFactor={6} style={{ pointerEvents: 'auto' }}>
            <div
              onClick={(e) => { e.stopPropagation(); onSelect?.(data.id); }}
              style={{
                fontFamily: '-apple-system, system-ui, sans-serif',
                fontWeight: 700,
                fontSize: 22,
                color: '#aaa',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                padding: '4px 16px',
                borderRadius: 8,
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#666'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#aaa'; }}
            >
              {data.name}
              <div style={{ fontSize: 11, color: '#bbb', marginTop: 2, fontWeight: 400 }}>
                click to collapse
              </div>
            </div>
          </Html>

          {data.children.map((child) => (
            <ChildCard key={child.id} child={child} />
          ))}

          {/* Waitlist CTA — styled like a ChildCard, positioned after last child */}
          <group position={[0, -(data.children.length > 0 ? 1.8 : 1.2), 0.1]}>
            <Html center distanceFactor={6} style={{ pointerEvents: 'auto' }}>
              <div
                onClick={(e) => { e.stopPropagation(); window.location.assign('/#beta'); }}
                style={{
                  width: 220,
                  padding: '14px 16px',
                  borderRadius: 10,
                  background: 'white',
                  border: '1px solid rgba(37, 99, 235, 0.15)',
                  boxShadow: '0 1px 8px rgba(37, 99, 235, 0.1)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(37, 99, 235, 0.4)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(37, 99, 235, 0.18)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(37, 99, 235, 0.15)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 8px rgba(37, 99, 235, 0.1)';
                }}
              >
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  color: '#FFFFFF',
                  fontWeight: 800,
                  flexShrink: 0,
                }}>
                  ◈
                </div>
                <div>
                  <div style={{
                    fontFamily: '-apple-system, system-ui, sans-serif',
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#2563EB',
                    lineHeight: 1.3,
                  }}>
                    Join the Beta
                  </div>
                  <div style={{
                    fontFamily: '-apple-system, system-ui, sans-serif',
                    fontSize: 11,
                    color: '#aaa',
                    marginTop: 1,
                  }}>
                    Get early access
                  </div>
                </div>
              </div>
            </Html>
          </group>
        </group>
      )}
    </group>
  );
}

// ============================================================
// CardContent — renders the card body based on unitType
// ============================================================

interface CardContentProps {
  data: UnitData;
  statusColor: string;
  health: number;
  ringSize: number;
  ringStroke: number;
  ringRadius: number;
  ringCircumference: number;
  ringDashoffset: number;
}

/** Shared: ring SVG used by deal, initiative, generic */
function StatusRing({ size, stroke, radius, circumference, dashoffset, statusColor, center }: {
  size: number; stroke: number; radius: number; circumference: number; dashoffset: number; statusColor: string; center: React.ReactNode;
}) {
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={statusColor} strokeWidth={stroke}
          strokeDasharray={circumference} strokeDashoffset={dashoffset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: '-apple-system, system-ui, sans-serif', fontWeight: 600, fontSize: 15, color: statusColor,
      }}>{center}</div>
    </div>
  );
}

/** Shared: people avatars row */
function AvatarRow({ children: kids }: { children: ChildPresence[] }) {
  const people = kids.filter(c => c.type === PresenceShape.PERSON);
  const items = kids.filter(c => c.type !== PresenceShape.PERSON);
  if (people.length === 0 && items.length === 0) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 2 }}>
      {people.map((person, i) => (
        <div key={person.id} style={{
          width: 28, height: 28, borderRadius: '50%',
          background: person.isGhost ? 'repeating-linear-gradient(45deg, #e0e0e0, #e0e0e0 2px, #f0f0f0 2px, #f0f0f0 4px)' : '#e8e8e8',
          border: '2px solid white', marginLeft: i > 0 ? -8 : 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: '-apple-system, system-ui, sans-serif', fontWeight: 600, fontSize: 10,
          color: person.isGhost ? '#bbb' : '#666', position: 'relative', zIndex: 10 - i,
          fontStyle: person.isGhost ? 'italic' : 'normal',
        }}>
          {person.initials || person.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
        </div>
      ))}
      {items.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginLeft: 8, alignItems: 'center' }}>
          {items.map(child => (
            <div key={child.id} style={{
              display: 'flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 4,
              background: 'rgba(0,0,0,0.04)', fontFamily: '-apple-system, system-ui, sans-serif', fontSize: 9, color: '#999',
            }}>
              <span style={{ fontSize: 10 }}>{getTypeIcon(child.type)}</span>
              {child.label && <span>{child.label}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Shared: activity timestamp row */
function ActivityRow({ lastActivity, recentChange, statusColor }: { lastActivity?: string; recentChange?: boolean; statusColor: string }) {
  if (!lastActivity) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontFamily: '-apple-system, system-ui, sans-serif', color: '#aaa', fontWeight: 400 }}>
        {recentChange && (
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, display: 'inline-block', flexShrink: 0, animation: 'activityPulse 2s ease-in-out infinite' }} />
        )}
        {lastActivity}
      </div>
    </div>
  );
}

/** Shared: bottom momentum bar */
function MomentumBar({ fill, color }: { fill: number; color: string }) {
  return (
    <div style={{ height: 3, borderRadius: 2, background: 'rgba(0,0,0,0.04)', marginTop: 'auto', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${fill * 100}%`, borderRadius: 2, background: `linear-gradient(90deg, ${color}, ${color}88)`, transition: 'width 0.5s ease' }} />
    </div>
  );
}

/** Shared: pulse keyframe (injected once per card that needs it) */
function PulseStyle() {
  return (
    <style>{`
      @keyframes activityPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.4; transform: scale(0.8); }
      }
    `}</style>
  );
}

function CardContent({ data, statusColor, health, ringSize, ringStroke, ringRadius, ringCircumference, ringDashoffset }: CardContentProps) {
  const ut = data.unitType ?? UnitType.DEAL; // fallback for existing data
  const childCount = data.children.length;
  const nameSize = 16 + data.heat * 4;
  const F = '-apple-system, system-ui, sans-serif';

  // Name block — shared across all types
  const nameBlock = (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: F, fontWeight: 600, fontSize: nameSize, color: '#1a1a1a', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {data.name}
      </div>
      {(data.facetLabel || data.subtitle) && (
        <div style={{ fontFamily: F, fontWeight: 400, fontSize: 12.5, color: '#888', marginTop: 3, lineHeight: 1.3 }}>
          {data.facetLabel || data.subtitle}
        </div>
      )}
    </div>
  );

  // Status tag chip (shared for types that use it)
  const statusChip = data.statusTag ? (
    <div style={{
      fontFamily: F, fontWeight: 600, fontSize: 10, color: statusColor,
      padding: '2px 8px', borderRadius: 4, background: `${statusColor}14`,
      textTransform: 'uppercase' as const, letterSpacing: 0.5, whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {data.statusTag}
    </div>
  ) : null;

  // ===== DEAL — dollar value, health ring, sales-oriented =====
  if (ut === UnitType.DEAL) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <StatusRing size={ringSize} stroke={ringStroke} radius={ringRadius} circumference={ringCircumference} dashoffset={ringDashoffset} statusColor={statusColor}
            center={<span style={{ fontSize: 15 + data.heat * 4 }}>{data.icon || childCount}</span>} />
          {nameBlock}
          {data.value && (
            <div style={{ textAlign: 'right' as const, flexShrink: 0 }}>
              {data.valueLabel && <div style={{ fontFamily: F, fontSize: 9, color: '#aaa', textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>{data.valueLabel}</div>}
              <div style={{ fontFamily: F, fontWeight: 700, fontSize: 18 + data.heat * 6, color: statusColor, whiteSpace: 'nowrap' }}>{data.value}</div>
            </div>
          )}
        </div>
        <AvatarRow>{data.children}</AvatarRow>
        <ActivityRow lastActivity={data.lastActivity} recentChange={data.recentChange} statusColor={statusColor} />
        <MomentumBar fill={data.heat} color={statusColor} />
        {data.recentChange && <PulseStyle />}
      </>
    );
  }

  // ===== PROJECT — progress percentage, milestone bar =====
  if (ut === UnitType.PROJECT) {
    const pct = data.progress != null ? data.progress : health;
    const pctLabel = `${Math.round(pct * 100)}%`;
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Circular progress instead of health ring */}
          <StatusRing size={ringSize} stroke={ringStroke} radius={ringRadius} circumference={ringCircumference}
            dashoffset={ringCircumference * (1 - pct)} statusColor={statusColor}
            center={<span style={{ fontSize: 13, fontWeight: 700 }}>{pctLabel}</span>} />
          {nameBlock}
          {statusChip || (data.value && (
            <div style={{ fontFamily: F, fontWeight: 600, fontSize: 13, color: '#666', whiteSpace: 'nowrap', flexShrink: 0 }}>{data.value}</div>
          ))}
        </div>
        {/* Wide progress bar */}
        <div style={{ marginTop: 4 }}>
          <div style={{ height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct * 100}%`, borderRadius: 3, background: statusColor, transition: 'width 0.5s ease' }} />
          </div>
        </div>
        <AvatarRow>{data.children}</AvatarRow>
        <ActivityRow lastActivity={data.lastActivity} recentChange={data.recentChange} statusColor={statusColor} />
        {data.recentChange && <PulseStyle />}
      </>
    );
  }

  // ===== PERSON — avatar-centric, role + relationship context =====
  if (ut === UnitType.PERSON) {
    const initials = data.icon || data.name.split(' ').map(w => w[0]).join('').slice(0, 2);
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: ringSize, height: ringSize, borderRadius: '50%',
            background: data.isGhost ? 'repeating-linear-gradient(45deg, #e0e0e0, #e0e0e0 2px, #f0f0f0 2px, #f0f0f0 4px)' : '#e8e8e8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: F, fontWeight: 700, fontSize: 18, color: data.isGhost ? '#bbb' : '#555',
            flexShrink: 0, border: `2px solid ${statusColor}40`,
          }}>
            {initials}
          </div>
          {nameBlock}
          {statusChip}
        </div>
        {/* Relationship items */}
        {childCount > 0 && (
          <div style={{ display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' as const }}>
            {data.children.map(child => (
              <div key={child.id} style={{
                display: 'flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 4,
                background: 'rgba(0,0,0,0.04)', fontFamily: F, fontSize: 10, color: '#888',
              }}>
                <span style={{ fontSize: 10 }}>{getTypeIcon(child.type)}</span>
                {child.label || child.name}
              </div>
            ))}
          </div>
        )}
        <ActivityRow lastActivity={data.lastActivity} recentChange={data.recentChange} statusColor={statusColor} />
        <MomentumBar fill={data.heat} color={statusColor} />
        {data.recentChange && <PulseStyle />}
      </>
    );
  }

  // ===== DOCUMENT — doc icon, status, last editor =====
  if (ut === UnitType.DOCUMENT) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: ringSize * 0.85, height: ringSize, borderRadius: 4,
            background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, flexShrink: 0,
          }}>
            {data.icon || '\u{1F4C4}'}
          </div>
          {nameBlock}
          {statusChip || (data.value && (
            <div style={{ fontFamily: F, fontWeight: 600, fontSize: 12, color: '#888', whiteSpace: 'nowrap', flexShrink: 0 }}>{data.value}</div>
          ))}
        </div>
        <AvatarRow>{data.children}</AvatarRow>
        <ActivityRow lastActivity={data.lastActivity} recentChange={data.recentChange} statusColor={statusColor} />
        <MomentumBar fill={data.heat} color={statusColor} />
        {data.recentChange && <PulseStyle />}
      </>
    );
  }

  // ===== INITIATIVE — OKR/strategic, progress arc + status =====
  if (ut === UnitType.INITIATIVE) {
    const pct = data.progress != null ? data.progress : health;
    const pctLabel = `${Math.round(pct * 100)}%`;
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <StatusRing size={ringSize} stroke={ringStroke} radius={ringRadius} circumference={ringCircumference}
            dashoffset={ringCircumference * (1 - pct)} statusColor={statusColor}
            center={<span style={{ fontSize: 13, fontWeight: 700 }}>{data.icon || pctLabel}</span>} />
          {nameBlock}
          {statusChip}
        </div>
        {data.value && (
          <div style={{ fontFamily: F, fontSize: 12, color: '#777', marginTop: 2 }}>
            {data.valueLabel ? `${data.valueLabel}: ` : ''}{data.value}
          </div>
        )}
        <AvatarRow>{data.children}</AvatarRow>
        <ActivityRow lastActivity={data.lastActivity} recentChange={data.recentChange} statusColor={statusColor} />
        {/* Wide progress bar */}
        <div style={{ height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.05)', marginTop: 'auto', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct * 100}%`, borderRadius: 2, background: statusColor, transition: 'width 0.5s ease' }} />
        </div>
        {data.recentChange && <PulseStyle />}
      </>
    );
  }

  // ===== GENERIC / fallback =====
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <StatusRing size={ringSize} stroke={ringStroke} radius={ringRadius} circumference={ringCircumference} dashoffset={ringDashoffset} statusColor={statusColor}
          center={<span style={{ fontSize: 15 + data.heat * 4 }}>{data.icon || (childCount > 0 ? childCount : '\u{25C6}')}</span>} />
        {nameBlock}
        {data.value && (
          <div style={{ fontFamily: F, fontWeight: 600, fontSize: 14, color: '#666', whiteSpace: 'nowrap', flexShrink: 0 }}>{data.value}</div>
        )}
      </div>
      <AvatarRow>{data.children}</AvatarRow>
      <ActivityRow lastActivity={data.lastActivity} recentChange={data.recentChange} statusColor={statusColor} />
      <MomentumBar fill={data.heat} color={statusColor} />
      {data.recentChange && <PulseStyle />}
    </>
  );
}

// ============================================================
// Child card — inside expanded unit
// ============================================================

function ChildCard({ child }: { child: ChildPresence }) {
  const groupRef = useRef<THREE.Group>(null);
  const time = useRef(Math.random() * 100);
  const baseScale = child.scale || 0.5;

  const opacity = useMemo(() => {
    if (child.isGhost) return 0.3;
    return 0.7 + child.heat * 0.3;
  }, [child.heat, child.isGhost]);

  useFrame((_, delta) => {
    time.current += delta;
    if (groupRef.current) {
      const t = time.current;
      const spd = 2 + child.heat * 4;
      const amp = 0.002 + child.heat * 0.005;
      const wiggleY = Math.sin(t * spd + child.offset[0]) * amp;
      groupRef.current.position.x = child.offset[0];
      groupRef.current.position.y = child.offset[1] + wiggleY;
    }
  });

  const width = 180 + baseScale * 60;

  return (
    <group ref={groupRef} position={[child.offset[0], child.offset[1], 0.1]}>
      <Html center distanceFactor={6} style={{ pointerEvents: 'auto' }}>
        <div style={{
          width,
          padding: '14px 16px',
          borderRadius: 10,
          background: child.isGhost ? 'rgba(255,255,255,0.6)' : 'white',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
          opacity,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          {/* Type icon */}
          {child.type === PresenceShape.PERSON ? (
            <div style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: child.isGhost
                ? 'repeating-linear-gradient(45deg, #e0e0e0, #e0e0e0 2px, #f0f0f0 2px, #f0f0f0 4px)'
                : '#e8e8e8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '-apple-system, system-ui, sans-serif',
              fontWeight: 600,
              fontSize: 11,
              color: child.isGhost ? '#bbb' : '#666',
              flexShrink: 0,
              fontStyle: child.isGhost ? 'italic' : 'normal',
            }}>
              {child.initials || child.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
            </div>
          ) : (
            <div style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: 'rgba(0,0,0,0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              flexShrink: 0,
            }}>
              {getTypeIcon(child.type)}
            </div>
          )}

          {/* Name + label */}
          <div>
            <div style={{
              fontFamily: '-apple-system, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 14,
              color: child.isGhost ? '#999' : '#333',
              lineHeight: 1.3,
              fontStyle: child.isGhost ? 'italic' : 'normal',
            }}>
              {child.name}
            </div>
            {child.label && (
              <div style={{
                fontFamily: '-apple-system, system-ui, sans-serif',
                fontSize: 11,
                color: '#aaa',
                marginTop: 1,
                textTransform: 'uppercase' as const,
                letterSpacing: 0.3,
              }}>
                {child.label}
              </div>
            )}
          </div>
        </div>
      </Html>
    </group>
  );
}

// ============================================================
// Type icons — simple, clean
// ============================================================

function getTypeIcon(type: PresenceShape): string {
  switch (type) {
    case PresenceShape.DEAL: return '\u{1F4B0}';
    case PresenceShape.DOCUMENT: return '\u{1F4C4}';
    case PresenceShape.PROJECT: return '\u{1F4CB}';
    case PresenceShape.PERSON: return '\u{1F464}';
    default: return '';
  }
}
